// ============================================================================
// FSA-2.2 — incoming cross-note line sync (target side)
// ============================================================================
// A cross-note flow is materialised on the TARGET Note as a `cross_note`
// line item whose local_id is `xn::<srcSchedule>::<srcClass>::<srcLine|_class>`
// (see FSA-Note-Statement-Dataflow.md § 5). The amount is SOURCE-OWNED: the
// auditor cannot type it, so the saved copy is only a snapshot of the source
// at the target's last save.
//
// Saved-as-primary still governs: the hydrate merge never rewrites a saved
// copy. Instead the Note Modal
//   • DIFFS the saved copies against the live aggregator (diffCrossNoteLines)
//     → amber live value per row, "source removed" / "duplicate" chips,
//     Deviations entry, Save enabled;
//   • SYNCS them (syncCrossNoteLines) only on an auditor action — the
//     "Update from sources" button, or Save (auto-applied, exactly like
//     applyAllLedgerSnapshots on ledger drift).
// So a saved value changes only when the auditor saves with the change shown.
//
// Why this exists (2026-10-09): copies were "saved snapshot wins" with no
// drift path on Save and no orphan pruning. A source change (Trade
// Receivables sweep) never reached Other Current Liabilities, and re-creating
// LT loan lines (new local_ids / new class) left the old copies beside the new
// ones — 4 lines for 2 loans in ST Borrowings once saved.
//
// Pure functions — unit-tested in crossNoteSync.test.ts.

import type {
  NoteClassData,
  NoteClassLive,
  NoteIncomingCrossNote,
  NoteLineItem,
} from 'api/types/fsa/noteData.types';
import { autoRound, keepRounded } from './rounding';

const EPS = 0.005;

/** Deterministic identity of a materialised incoming cross-note line. */
export const crossNoteLocalId = (r: NoteIncomingCrossNote): string =>
  `xn::${r.sourceScheduleCode}::${r.sourceClassCode}::${r.sourceLineItemLocalId ?? '_class'}`;

export type CrossNoteLineStatus =
  | { kind: 'in_sync' }
  | { kind: 'changed'; liveCy: number; livePy: number }
  /** Source line / class no longer routes here (deleted, moved class, cross-note cleared). */
  | { kind: 'orphan' }
  /** Second copy of the same local_id in the same class — only the first one is kept. */
  | { kind: 'duplicate' };

export interface CrossNoteDiff {
  /**
   * Status per saved cross_note line, keyed `${classCode}|${localId}|${index}`
   * where index is the line's position in class_data[classCode].line_items.
   */
  lineStatus: Map<string, CrossNoteLineStatus>;
  /** Live refs with no saved copy in their target class. */
  missing: NoteIncomingCrossNote[];
  changedCount: number;
  orphanCount: number;
  duplicateCount: number;
  hasDrift: boolean;
}

/** Key used by lineStatus — index disambiguates duplicate local_ids. */
export const crossNoteLineKey = (classCode: string, localId: string, index: number): string =>
  `${classCode}|${localId}|${index}`;

/** Live refs that can land on this Note, indexed by `${targetClass}|${localId}`. */
function indexLiveRefs(liveRefs: readonly NoteIncomingCrossNote[]): Map<string, NoteIncomingCrossNote> {
  const m = new Map<string, NoteIncomingCrossNote>();
  for (const r of liveRefs) {
    if (!r.targetClassCode) continue; // unassigned refs are not materialised
    const key = `${r.targetClassCode}|${crossNoteLocalId(r)}`;
    if (!m.has(key)) m.set(key, r);
  }
  return m;
}

/**
 * Compare the workspace's saved cross_note lines with the live aggregator.
 * Never mutates. A line is matched on (class it sits in, local_id): a copy
 * sitting in a class the source no longer targets is an orphan.
 */
export function diffCrossNoteLines(
  classData: Record<string, NoteClassData>,
  liveRefs: readonly NoteIncomingCrossNote[],
): CrossNoteDiff {
  const live = indexLiveRefs(liveRefs);
  const lineStatus = new Map<string, CrossNoteLineStatus>();
  const seenLocalIds = new Set<string>();
  const matchedLiveKeys = new Set<string>();
  let changedCount = 0, orphanCount = 0, duplicateCount = 0;

  for (const [classCode, cd] of Object.entries(classData)) {
    const items = cd.line_items ?? [];
    for (let index = 0; index < items.length; index += 1) {
      const li = items[index];
      if (li.allocation_mode !== 'cross_note') continue;
      const key = crossNoteLineKey(classCode, li.local_id, index);
      // Duplicates are counted per class: a copy of the same ref in ANOTHER
      // class is not a duplicate but an orphan (wrong class) — see below.
      const liveKey = `${classCode}|${li.local_id}`;
      if (seenLocalIds.has(liveKey)) {
        lineStatus.set(key, { kind: 'duplicate' });
        duplicateCount += 1;
        continue;
      }
      seenLocalIds.add(liveKey);
      const ref = live.get(liveKey);
      if (!ref) {
        lineStatus.set(key, { kind: 'orphan' });
        orphanCount += 1;
        continue;
      }
      matchedLiveKeys.add(liveKey);
      const liveCy = ref.amountCy ?? 0;
      const livePy = ref.amountPy ?? 0;
      if (Math.abs((li.amount_actual ?? 0) - liveCy) > EPS
        || Math.abs((li.prior_amount_actual ?? 0) - livePy) > EPS) {
        lineStatus.set(key, { kind: 'changed', liveCy, livePy });
        changedCount += 1;
      } else {
        lineStatus.set(key, { kind: 'in_sync' });
      }
    }
  }

  const missing: NoteIncomingCrossNote[] = [];
  for (const [liveKey, ref] of live) {
    if (!matchedLiveKeys.has(liveKey)) missing.push(ref);
  }

  return {
    lineStatus,
    missing,
    changedCount,
    orphanCount,
    duplicateCount,
    hasDrift: changedCount + orphanCount + duplicateCount + missing.length > 0,
  };
}

const blankClassData = (): NoteClassData => ({
  shares: null, share_type: null, face_value: null, paid_status: null,
  remarks: null, hide_in_print: false,
  amount_actual: 0, amount_rounded_auto: 0, amount_rounded: 0,
  prior_amount_actual: 0, prior_amount_rounded_auto: 0, prior_amount_rounded: 0,
  ledgers: [], line_items: [],
});

/**
 * Bring every cross_note line in line with the live aggregator:
 *   • changed amount → refreshed (rounded value kept while the actual is
 *     unchanged — keepRounded, same rule as Update from Ledger);
 *   • orphan / duplicate copy → dropped;
 *   • live ref with no copy → appended to its target class.
 * Preserved on refreshed lines: position, dimension values, remarks, and the
 * auditor's rename of a CLASS-level copy (line-level copies take the source
 * line's name — FSA-Module.md hard rules).
 * Classes whose cross_note lines changed get their totals re-rolled from
 * their line items (Option A). Returns the input object when nothing changed.
 */
export function syncCrossNoteLines(
  classData: Record<string, NoteClassData>,
  liveRefs: readonly NoteIncomingCrossNote[],
  factor: string | null | undefined,
): Record<string, NoteClassData> {
  const diff = diffCrossNoteLines(classData, liveRefs);
  if (!diff.hasDrift) return classData;

  const next: Record<string, NoteClassData> = { ...classData };
  const touched = new Set<string>();

  for (const [classCode, cd] of Object.entries(classData)) {
    const items = cd.line_items ?? [];
    if (!items.some(li => li.allocation_mode === 'cross_note')) continue;
    const kept: NoteLineItem[] = [];
    let changed = false;
    items.forEach((li, index) => {
      if (li.allocation_mode !== 'cross_note') { kept.push(li); return; }
      const status = diff.lineStatus.get(crossNoteLineKey(classCode, li.local_id, index));
      if (!status || status.kind === 'in_sync') { kept.push(li); return; }
      changed = true;
      if (status.kind === 'orphan' || status.kind === 'duplicate') return; // drop
      const ref = liveRefs.find(r =>
        r.targetClassCode === classCode && crossNoteLocalId(r) === li.local_id)!;
      const cyAuto = autoRound(status.liveCy, factor);
      const pyAuto = autoRound(status.livePy, factor);
      kept.push({
        ...li,
        name: ref.sourceLineItemLocalId
          ? (ref.sourceLineItemName || ref.label || li.name)
          : li.name,
        amount_actual: status.liveCy,
        amount_rounded_auto: cyAuto,
        amount_rounded: keepRounded(li.amount_actual, status.liveCy, li.amount_rounded, cyAuto),
        prior_amount_actual: status.livePy,
        prior_amount_rounded_auto: pyAuto,
        prior_amount_rounded: keepRounded(li.prior_amount_actual, status.livePy, li.prior_amount_rounded, pyAuto),
      });
    });
    if (changed) {
      next[classCode] = { ...cd, line_items: kept };
      touched.add(classCode);
    }
  }

  for (const ref of diff.missing) {
    const classCode = ref.targetClassCode!;
    const cd = next[classCode] ?? blankClassData();
    const items = cd.line_items ?? [];
    const cy = ref.amountCy ?? 0;
    const py = ref.amountPy ?? 0;
    next[classCode] = {
      ...cd,
      line_items: [
        ...items,
        {
          local_id: crossNoteLocalId(ref),
          name: ref.sourceLineItemName || ref.label || 'Cross-note',
          display_order: (items.length + 1) * 10,
          allocation_mode: 'cross_note',
          ledgers: [],
          manual_amount_actual: null,
          source_ref: {
            schedule_code: ref.sourceScheduleCode,
            schedule_name: ref.sourceScheduleName,
            class_code: ref.sourceClassCode,
            line_item_local_id: ref.sourceLineItemLocalId ?? null,
            line_item_name: ref.sourceLineItemName ?? null,
          },
          amount_actual: cy,
          amount_rounded_auto: autoRound(cy, factor),
          amount_rounded: autoRound(cy, factor),
          prior_amount_actual: py,
          prior_amount_rounded_auto: autoRound(py, factor),
          prior_amount_rounded: autoRound(py, factor),
        },
      ],
    };
    touched.add(classCode);
  }

  for (const classCode of touched) {
    const cd = next[classCode];
    // An emptied class (it only held copies that were dropped) rolls to 0 —
    // the same Σ over no line items.
    const items = cd.line_items ?? [];
    const sum = (f: 'amount_actual' | 'prior_amount_actual' | 'amount_rounded' | 'prior_amount_rounded') =>
      Math.round(items.reduce((s, li) => s + (li[f] ?? 0), 0) * 100) / 100;
    const cy = sum('amount_actual');
    const py = sum('prior_amount_actual');
    next[classCode] = {
      ...cd,
      amount_actual: cy,
      prior_amount_actual: py,
      amount_rounded: sum('amount_rounded'),
      prior_amount_rounded: sum('prior_amount_rounded'),
      amount_rounded_auto: autoRound(cy, factor),
      prior_amount_rounded_auto: autoRound(py, factor),
    };
  }
  return next;
}

// ----------------------------------------------------------------------------
// Source side — auto sign-sweep (Trade Receivables / Trade Payables)
// ----------------------------------------------------------------------------
// The backend stamps class.cross_note_amount on SAVE from the consolidated TB
// (EngagementNoteDataService.ApplyAutoCrossNoteSweepAsync). If only the sweep
// moved — a ledger excluded / re-included, a sign flip with an unchanged net —
// nothing else marks the source Note as needing a save, so the stale sweep
// kept flowing to the target. These mirror the repository query
// (GetSignAnomalousSumsByClassAsync): raw signed sum of the anomalous side per
// template class, control accounts + excluded ledgers skipped, MEMO_ONLY
// classes skipped, and only classes present in the workspace are stamped.

/** Live per-class sweep, in the source schedule's stored sign. */
export function liveSignSweepByClass(
  liveClasses: readonly NoteClassLive[],
  isCreditBalance: boolean,
): Map<string, { cy: number; py: number }> {
  const out = new Map<string, { cy: number; py: number }>();
  for (const c of liveClasses) {
    if (c.classRole === 'MEMO_ONLY') continue;
    let cy = 0, py = 0;
    for (const l of c.ledgers ?? []) {
      if (l.isControlAccount || l.excludeFromSignSweep) continue;
      const closing = Number(l.closingActual ?? 0);
      const opening = Number(l.openingActual ?? 0);
      cy += isCreditBalance ? Math.max(closing, 0) : Math.min(closing, 0);
      py += isCreditBalance ? Math.max(opening, 0) : Math.min(opening, 0);
    }
    out.set(c.classCode, { cy: Math.round(cy * 100) / 100, py: Math.round(py * 100) / 100 });
  }
  return out;
}

/** TRUE when a saved class sweep differs from what the next save will stamp. */
export function hasSignSweepDrift(
  classData: Record<string, NoteClassData>,
  liveClasses: readonly NoteClassLive[],
  isCreditBalance: boolean,
): boolean {
  for (const [classCode, live] of liveSignSweepByClass(liveClasses, isCreditBalance)) {
    const cd = classData[classCode];
    if (!cd) continue;
    if (Math.abs((cd.cross_note_amount ?? 0) - live.cy) > EPS) return true;
    if (Math.abs((cd.prior_cross_note_amount ?? 0) - live.py) > EPS) return true;
  }
  return false;
}
